import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Target, Wallet, Loader2 } from 'lucide-react';

const empty = { code: '', name: '', name_arabic: '', description: '', parent_id: '', is_active: true };

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CostCenters = ({ user }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ccRes, sumRes] = await Promise.all([
        axiosInstance.get('/cost-centers'),
        axiosInstance.get('/accounting/cost-center-summary'),
      ]);
      setRows(ccRes.data);
      const map = {};
      sumRes.data.cost_centers.forEach((s) => { map[s.cost_center_id] = s; });
      setSummary(map);
    } catch (e) {
      toast.error(`فشل التحميل: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setForm(empty); setEditingId(null); setShowModal(true); };
  const openEdit = (row) => {
    setForm({
      code: row.code || '',
      name: row.name || '',
      name_arabic: row.name_arabic || '',
      description: row.description || '',
      parent_id: row.parent_id || '',
      is_active: row.is_active !== false,
    });
    setEditingId(row.id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('الكود والاسم مطلوبان');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (editingId) {
        await axiosInstance.put(`/cost-centers/${editingId}`, payload);
        toast.success('تم تحديث مركز التكلفة');
      } else {
        await axiosInstance.post('/cost-centers', payload);
        toast.success('تم إنشاء مركز التكلفة');
      }
      setShowModal(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحفظ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('حذف مركز التكلفة؟ لا يمكن التراجع.')) return;
    try {
      await axiosInstance.delete(`/cost-centers/${id}`);
      toast.success('تم الحذف');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحذف');
    }
  };

  return (
    <div className="space-y-6" data-testid="cost-centers-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-7 h-7 text-indigo-600" /> Cost Centers
          </h1>
          <p className="text-sm text-gray-500 mt-1">مراكز التكلفة — تصنيف موازي للحركات المالية</p>
        </div>
        {isAdmin && (
          <button data-testid="new-cc-btn" onClick={openCreate}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Cost Center
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-400">لا توجد مراكز تكلفة بعد. أنشئ أولها لتبدأ تتبع التكاليف بشكل مفصّل.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Code</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Name</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">الاسم العربي</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Debit</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Credit</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Net</th>
                <th className="px-3 py-2 text-center text-[11px] uppercase text-gray-500 font-semibold">Status</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = summary[r.id] || { debit: 0, credit: 0, net: 0 };
                return (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-sm">{r.code}</td>
                    <td className="px-3 py-2 text-sm">{r.name}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{r.name_arabic || '—'}</td>
                    <td className="px-3 py-2 text-sm text-right font-mono text-emerald-700">{fmt(s.debit)}</td>
                    <td className="px-3 py-2 text-sm text-right font-mono text-red-700">{fmt(s.credit)}</td>
                    <td className={`px-3 py-2 text-sm text-right font-mono font-semibold ${s.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(s.net)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded ${r.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {isAdmin && (
                          <>
                            <button data-testid={`edit-cc-${r.code}`} onClick={() => openEdit(r)}
                              className="p-1.5 hover:bg-gray-100 rounded text-blue-600"><Edit2 className="w-4 h-4" /></button>
                            <button data-testid={`delete-cc-${r.code}`} onClick={() => handleDelete(r.id)}
                              className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-testid="cc-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold">{editingId ? 'Edit' : 'New'} Cost Center</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Code *</label>
                <input data-testid="cc-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono" required />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Name *</label>
                <input data-testid="cc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" required />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">الاسم العربي</label>
                <input value={form.name_arabic} onChange={(e) => setForm({ ...form, name_arabic: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" rows={2} />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Parent Cost Center</label>
                <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                  <option value="">— None (root) —</option>
                  {rows.filter((p) => p.id !== editingId).map((p) => (
                    <option key={p.id} value={p.id}>{p.code} • {p.name}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                Active
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="cc-submit" disabled={submitting}
                  className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                  {submitting ? 'Saving…' : (editingId ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostCenters;
